import Button from 'ext-ui/components/Button/Button'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import React from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import * as style from 'src/app/CardFeedCreate/CardFeedCreate.style'
import ErrorList from 'src/components/ErrorList/ErrorList'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgChevronLeft from 'src/components/Svgs/SvgChevronLeft'
import { useCurrentBrand } from 'src/context/ConfigContext/useConfig'
import { useTranslate } from 'src/localization/Trns'
import { termsUrl } from 'src/navigation/urls'

import type { CardNetwork } from './CardFeedCreate'

interface StepReviewTermsProps {
  network: CardNetwork | undefined
  termsAccepted: boolean
  onTermsAcceptedChange: (accepted: boolean) => void
  onBack: () => void
  onConfirm: () => void
  isSubmitting: boolean
  submitError: string | undefined
}

const networkDisplayName = (
  network: CardNetwork | undefined,
  translate: (key: string) => string,
): string => {
  if (network === 'visa') return translate('Visa')
  return translate('Mastercard')
}

const StepReviewTerms: React.FC<StepReviewTermsProps> = ({
  network,
  termsAccepted,
  onTermsAcceptedChange,
  onBack,
  onConfirm,
  isSubmitting,
  submitError,
}) => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const { EXTERNAL_TERMS_URL: externalTermsUrl } = useCurrentBrand()
  const tosUrl = externalTermsUrl ?? termsUrl()

  const networkName = networkDisplayName(network, translate)

  const handleAcknowledgeToggle = (): void => {
    const next = !termsAccepted
    onTermsAcceptedChange(next)
    analytics.track('card feeds create', {
      action: 'terms toggled',
      accepted: next,
      step: 3,
    })
  }

  const handleBackClick = (): void => {
    analytics.track('card feeds create', { action: 'back clicked', step: 3 })
    onBack()
  }

  const handleConfirmClick = (): void => {
    analytics.track('card feeds create - confirm & connect', { step: 3 })
    onConfirm()
  }

  return (
    <div>
      <header css={style.stepHeader}>
        <span css={style.stepEyebrow}>{translate('Step 3 of 3')}</span>
        <p css={style.stepHeading}>
          {translate('Review and accept your enrollment terms')}
        </p>
        <p css={style.stepBody}>
          {translate(
            "A quick review before we connect. These terms describe how Extend handles your card data and what your bank's responsibilities are.",
          )}
        </p>
      </header>

      <section css={style.termsBox} aria-labelledby="enrollment-terms-intro">
        <span id="enrollment-terms-intro" css={style.termsBoxIntro}>
          {translate(
            'By enrolling your company & clicking on "Confirm & Connect", you hereby agree to the following enrollment terms:',
          )}
        </span>
        <ul css={style.termsBoxList}>
          <li>
            {translate(
              'By sending us the bank feed data, you acknowledge that your company has obtained consent from individual cardholders, as recognized under applicable law, authorizing ${network} to access and share transaction details with Extend and your company to facilitate individual cardholders’ participation in the Extend service; and that individual cardholders may revoke such consent (i.e., opt out of sharing their payment card transaction details) at any time.',
              { network: networkName },
            )}
          </li>
          <li>
            {translate(
              'Allow Extend to create expenses on your behalf and enable program notifications.',
            )}
          </li>
        </ul>
      </section>

      <div css={style.termsAcceptRow} data-testid="card-feed-create-terms-row">
        <Checkbox
          id="card-feed-create-terms-checkbox-input"
          aria-label={translate(
            'I agree to the enrollment terms and acknowledge the Extend Terms of Service apply',
          )}
          checked={termsAccepted}
          onChange={handleAcknowledgeToggle}
          data-testid="card-feed-create-terms-checkbox"
        />
        <label htmlFor="card-feed-create-terms-checkbox-input">
          {translate(
            'You agree to the enrollment terms above and also acknowledge that your card feed enrollment will be subject to and governed by the ${tosLink}, which already apply to you and your company.',
            {
              tosLink: (
                <HoverableLink
                  href={tosUrl}
                  ariaLabelSource="Extend Terms of Service"
                  testId="card-feed-create-tos-link"
                  regular
                  underline
                >
                  {translate('Extend Terms of Service')}
                </HoverableLink>
              ),
            },
          )}
        </label>
      </div>

      {submitError !== undefined && (
        <div data-testid="card-feed-create-submit-error">
          <ErrorList errors={[submitError]} />
        </div>
      )}

      <div css={style.stepActions}>
        <button
          type="button"
          css={style.backButton}
          onClick={handleBackClick}
          disabled={isSubmitting}
          data-testid="card-feed-create-back-3"
        >
          <SvgChevronLeft
            width={16}
            height={16}
            viewBox="0 0 24 24"
            aria-hidden="true"
          />
          {translate('Go Back')}
        </button>
        <Button
          variant="primary"
          onClick={handleConfirmClick}
          disabled={!termsAccepted || isSubmitting}
          data-testid="card-feed-create-confirm"
        >
          {isSubmitting
            ? translate('Connecting...')
            : translate('Confirm & Connect')}
        </Button>
      </div>
    </div>
  )
}

export default StepReviewTerms
